export type ControlTone = "green" | "amber" | "red" | "grey";

export type PassportCheckpoint =
  | "requisition"
  | "approval"
  | "rfq"
  | "quotation"
  | "comparison"
  | "award"
  | "purchase_order"
  | "acknowledgement"
  | "receipt"
  | "invoice"
  | "payment";

export type PassportRequirement = {
  requirement_code: string;
  label?: string | null;
  requirement_state: string;
  evidence_type?: string | null;
  source_entity_type?: string | null;
  source_entity_id?: string | null;
};

export type PassportSnapshot = {
  id: string;
  version: number;
  payload?: Record<string, any> | null;
  missing_count?: number | null;
  open_exception_count?: number | null;
  completeness_percent?: number | null;
  generated_at?: string | null;
  sha256?: string | null;
};

const checkpointCodes: Record<PassportCheckpoint, string[]> = {
  requisition: ["request"],
  approval: ["request", "approval"],
  rfq: ["sourcing"],
  quotation: ["sourcing"],
  comparison: ["sourcing"],
  award: ["supplier_selection", "supplier_qualification"],
  purchase_order: ["order"],
  acknowledgement: ["supplier_acknowledgement"],
  receipt: ["receipt_or_acceptance"],
  invoice: ["invoice", "matching"],
  payment: ["payment_approval", "payment_evidence"],
};

const resolution: Record<string, { next: string; resolver: string; action: string; view: string }> = {
  request: { next: "Correct the request or add the missing business evidence.", resolver: "Requester or organisation administrator", action: "Correct information", view: "requisitions" },
  approval: { next: "Obtain the outstanding approval from the assigned authority.", resolver: "Assigned approver or organisation administrator", action: "Request additional approval", view: "approvals" },
  sourcing: { next: "Add an eligible quotation or complete the governed sourcing route.", resolver: "Procurement", action: "Add quotation", view: "rfqs" },
  supplier_selection: { next: "Select an eligible supplier, record the permitted reason, or request the authorised exception.", resolver: "Procurement and the policy-named approver", action: "Change supplier", view: "rfqs" },
  supplier_qualification: { next: "Complete supplier qualification or use a policy-permitted exception.", resolver: "Procurement or organisation administrator", action: "Add evidence", view: "vendors" },
  order: { next: "Issue the purchase order from the authorised award.", resolver: "Procurement", action: "Open purchase order", view: "orders" },
  supplier_acknowledgement: { next: "Ask the supplier to acknowledge, clarify or propose the delivery change.", resolver: "Invited supplier, followed by procurement", action: "Request acknowledgement", view: "orders" },
  receipt_or_acceptance: { next: "Record accepted and rejected quantities with the variance evidence.", resolver: "Receiver", action: "Record receipt variance", view: "receipts" },
  invoice: { next: "Record or correct the supplier invoice against the controlled order.", resolver: "Finance", action: "Correct information", view: "invoices" },
  matching: { next: "Resolve the quantity, rate or tax mismatch before approval.", resolver: "Finance, with procurement or receiver where required", action: "Resolve invoice mismatch", view: "invoices" },
  payment_approval: { next: "Obtain the authorised payment approval after matching is clear.", resolver: "Finance and the assigned payment approver", action: "Request additional approval", view: "money" },
  payment_evidence: { next: "Record externally executed payment and reconciliation evidence.", resolver: "Finance", action: "Add evidence", view: "money" },
};

export function toneForRequirement(state: string): ControlTone {
  if (state === "present") return "green";
  if (state === "accepted_exception") return "amber";
  if (state === "required") return "red";
  return "grey";
}

export function toneForPassport(
  snapshot?: PassportSnapshot | null,
  controlOrigin?: string | null,
): ControlTone {
  if (!snapshot || (controlOrigin && controlOrigin !== "native_machine_3a")) return "grey";
  if (Number(snapshot.missing_count || snapshot.payload?.missing_count || 0) > 0) return "red";
  if (
    Number(snapshot.open_exception_count || snapshot.payload?.open_exception_count || 0) > 0 ||
    Number(snapshot.payload?.accepted_exception_count || 0) > 0
  ) return "amber";
  return "green";
}

export function toneLabel(tone: ControlTone) {
  return {
    green: "Ready to proceed",
    amber: "Attention required",
    red: "Blocked until resolved",
    grey: "Not applicable or not yet evaluated",
  }[tone];
}

export function checkpointRequirements(
  snapshot: PassportSnapshot | undefined,
  checkpoint: PassportCheckpoint,
): PassportRequirement[] {
  const evidence = Array.isArray(snapshot?.payload?.evidence)
    ? (snapshot?.payload?.evidence as PassportRequirement[])
    : [];
  const expected = checkpointCodes[checkpoint];
  return evidence.filter((item) => expected.includes(item.requirement_code));
}

export function requirementExplanation(
  requirement: PassportRequirement,
  policy: { name?: string | null; version?: number | string | null; effectiveFrom?: string | null },
) {
  const fallback = resolution[requirement.requirement_code] || {
    next: "Add the evidence named by the active control.",
    resolver: "The role named by the organisation policy",
    action: "Add evidence",
    view: "control-passports",
  };
  const policyName = policy.name || "The evaluated organisation control policy";
  const version = policy.version ? ` · version ${policy.version}` : "";
  const effective = policy.effectiveFrom ? ` · effective ${policy.effectiveFrom}` : "";
  const state = toneForRequirement(requirement.requirement_state);
  return {
    tone: state,
    why:
      state === "green"
        ? `${requirement.label || requirement.requirement_code} is recorded for this checkpoint.`
        : state === "amber"
          ? `${requirement.label || requirement.requirement_code} proceeds through an approved exception that remains visible.`
          : state === "red"
            ? `${requirement.label || requirement.requirement_code} is required but is not recorded for this checkpoint.`
            : `${requirement.label || requirement.requirement_code} is not applicable or has not been evaluated for this record.`,
    policy: `${policyName}${version}${effective}`,
    ...fallback,
  };
}
